import { get, writable } from 'svelte/store';

type TravelState = {
    traveling: boolean;
    frozenUtcMs: number | null;
};

/**
 * Whether the current client is time-traveling and, if so, the frozen server instant. Reactive so
 * live displays (e.g. the clock) update when travel state changes. Defaults to "not traveling" —
 * real browser time — which is always the case in production. {@link timeTravelSync} keeps it in sync.
 *
 * This module is deliberately dependency-light (only svelte/store) so the read path stays cheap and
 * unit-testable; the API/auth/env wiring lives in `timeTravelSync.ts`.
 */
export const timeTravel = writable<TravelState>({ traveling: false, frozenUtcMs: null });

/**
 * The single source of "now" for the app. Returns the browser clock normally, and the frozen server
 * instant only while the current client is time-traveling (non-prod only). Synchronous and cheap — it
 * never calls the API; the cached travel state is refreshed on discrete triggers by `timeTravelSync`.
 */
export function now(): Date {
    return new Date(nowMs());
}

export function nowMs(): number {
    const state = get(timeTravel);
    return state.traveling && state.frozenUtcMs !== null ? state.frozenUtcMs : Date.now();
}
